import { useEffect, useMemo, useState } from 'react';

import { getDiscoveredCards, type CardCollectionState } from './cardCollectionStore';

type FilterType = 'all' | 'weapon' | 'passive' | 'special';

interface CardCollectionProps {
  state: CardCollectionState;
  onBack: () => void;
  onToggleEquip: (cardId: string) => void;
}

const FILTER_LABELS: Record<FilterType, string> = {
  all: 'All',
  weapon: 'Weapons',
  passive: 'Passives',
  special: 'Specials',
};

const RARITY_COLORS: Record<string, string> = {
  common: '#9aa2b2',
  rare: '#4ea4ff',
  epic: '#d171ff',
  legendary: '#ffca59',
};

export function CardCollection({ state, onBack, onToggleEquip }: CardCollectionProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const discoveredCards = useMemo(() => getDiscoveredCards(state), [state]);
  const filteredCards = useMemo(
    () => discoveredCards.filter((card) => filter === 'all' || card.type === filter),
    [discoveredCards, filter],
  );

  useEffect(() => {
    if (!filteredCards.length) {
      setSelectedCardId(null);
      return;
    }
    if (!selectedCardId || !filteredCards.some((card) => card.id === selectedCardId)) {
      setSelectedCardId(filteredCards[0].id);
    }
  }, [filteredCards, selectedCardId]);

  const selectedCard = filteredCards.find((card) => card.id === selectedCardId) ?? null;

  return (
    <div
      style={{
        minHeight: '100vh',
        maxWidth: 480,
        margin: '0 auto',
        padding: 16,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <button type="button" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ← Back
      </button>
      <h2 style={{ margin: 0 }}>Card Collection</h2>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(Object.keys(FILTER_LABELS) as FilterType[]).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setFilter(type)}
            style={{
              border: '1px solid #2f3f6b',
              background: filter === type ? '#2c5fa8' : '#16213e',
              color: '#fff',
              borderRadius: 10,
              padding: '8px 10px',
            }}
          >
            {FILTER_LABELS[type]}
          </button>
        ))}
      </div>

      {filteredCards.length === 0 ? (
        <div style={{ color: '#9fb3d8' }}>No discovered cards in this category yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {filteredCards.map((card) => {
            const isEquipped = state.equippedCardIds.includes(card.id);
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => setSelectedCardId(card.id)}
                style={{
                  border: `1px solid ${RARITY_COLORS[card.rarity]}`,
                  background: selectedCardId === card.id ? '#223863' : '#16213e',
                  color: '#fff',
                  borderRadius: 12,
                  padding: 12,
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>{card.icon}</span>
                  {isEquipped && <span style={{ fontSize: 11, color: '#90ffb1' }}>EQUIPPED</span>}
                </div>
                <div style={{ marginTop: 4, fontWeight: 700 }}>{card.name}</div>
                <div style={{ fontSize: 12, color: '#9fb3d8' }}>Lvl {state.ownedCounts[card.id] ?? 0}</div>
              </button>
            );
          })}
        </div>
      )}

      {selectedCard && (
        <div style={{ background: '#16213e', border: '1px solid #2f3f6b', borderRadius: 12, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong>
              {selectedCard.icon} {selectedCard.name}
            </strong>
            <span style={{ color: RARITY_COLORS[selectedCard.rarity] }}>{selectedCard.rarity.toUpperCase()}</span>
          </div>
          <div style={{ marginTop: 6, color: '#9fb3d8' }}>Type: {selectedCard.type}</div>
          <div style={{ marginTop: 6 }}>Level: {state.ownedCounts[selectedCard.id] ?? 0}</div>
          <div style={{ marginTop: 8 }}>{selectedCard.description}</div>
          <button
            type="button"
            onClick={() => onToggleEquip(selectedCard.id)}
            style={{ marginTop: 10, width: '100%', padding: 10, borderRadius: 10, border: '1px solid #2f3f6b' }}
          >
            {state.equippedCardIds.includes(selectedCard.id) ? 'Unequip' : 'Equip'}
          </button>
        </div>
      )}
    </div>
  );
}
